import { useMemo } from 'react';
import { UIMarket } from '@velocity-exchange/common';
import { User, FOUR, BigNum, MarketType } from '@velocity-exchange/sdk';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';
import useMarketMaxLeverage from 'src/hooks/useMarketMaxLeverages';

/**
 * Calculates max leverage for a perp position, returning the minimum of:
 * 1. User's account health-based max leverage
 * 2. User's personal max leverage cap (if set)
 */
export const useMaxLeverageAvailable = (props: {
	selectedMarket: UIMarket | null;
	currentUserClient: User | undefined;
	maxBaseAvailable: number;
	bufferedMinOrderSize: number;
	freeCollateral: number;
}) => {
	const {
		selectedMarket,
		currentUserClient,
		maxBaseAvailable,
		bufferedMinOrderSize,
		freeCollateral,
	} = props;

	const { maxLeverage } = useMarketMaxLeverage(
		MarketType.PERP,
		selectedMarket?.marketIndex
	);

	const userMaxLeverageForSelectedMarket = useMarketSettingsStore((s) =>
		s.getMaxLeverageForMarket(selectedMarket?.uiSymbols.marketSymbol)
	);

	return useMemo(() => {
		// If the new max base available is less than the minimum order size, set the max trade size to 0
		if (maxBaseAvailable < bufferedMinOrderSize) {
			return 0;
		}

		if (!currentUserClient) {
			return maxLeverage;
		}

		if (freeCollateral === 0) return 0;

		try {
			// Get account health-based max leverage
			const newMaxLeverage = BigNum.from(
				currentUserClient.getMaxLeverageForPerp(
					selectedMarket?.marketIndex ?? 0
				),
				FOUR
			).toNum();

			// Return min of account max and user's custom cap
			return Math.min(
				newMaxLeverage,
				userMaxLeverageForSelectedMarket ?? maxLeverage
			);
		} catch {
			// little race condition here after wallet disconnect so wrap in try/catch
			return maxLeverage;
		}
	}, [
		maxLeverage,
		selectedMarket?.marketIndex,
		currentUserClient,
		freeCollateral,
		bufferedMinOrderSize,
		maxBaseAvailable,
		userMaxLeverageForSelectedMarket,
	]);
};
